import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Ticket, Plus, Search, Filter, LayoutGrid, Table2, Tags, Timer, Building2 } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import api, { apiError } from "@/lib/api";
import TicketCard from "@/components/TicketCard";
import TicketTable from "@/components/TicketTable";
import TicketDialog from "@/components/TicketDialog";
import TicketTitlesDialog from "@/components/TicketTitlesDialog";
import TicketDetailDialog from "@/components/TicketDetailDialog";
import SlaDialog from "@/components/SlaDialog";
import NewLocationDialog from "@/components/NewLocationDialog";
import { TICKET_STATUS, TICKET_CATEGORY, TICKET_PRIORITY, statusMeta, sortTickets, isOverdue } from "@/lib/tickets";

export default function Tickets() {
  const { lang } = useLang();
  const de = lang === "de";
  const { user } = useAuth();
  const canEditTickets = can(user, "tickets", "edit");
  const canSla = can(user, "uebersicht", "edit");
  const canNewLoc = can(user, "standortantrag", "edit");
  const [tickets, setTickets] = useState([]);
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("open");   // all | open | <status>
  const [catF, setCatF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("overdue");
  const [view, setView] = useState(() => localStorage.getItem("tickets_view") || "cards");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [titlesDlg, setTitlesDlg] = useState(false);
  const [slaDlg, setSlaDlg] = useState(false);
  const [newLocDlg, setNewLocDlg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, dr, ur] = await Promise.all([api.get("/tickets"), api.get("/devices"), api.get("/users").catch(() => ({ data: [] }))]);
      setTickets(tr.data || []);
      setDevices(dr.data || []);
      setUsers(ur.data || []);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { locName, devicesByLoc, locations } = useMemo(() => {
    const nm = {}, byLoc = {};
    devices.forEach((d) => {
      const lid = d.location_id || d.device_code;
      if (!lid) return;
      (byLoc[lid] = byLoc[lid] || []).push(d);
      if (!nm[lid] && d.location_name) nm[lid] = d.location_name;
    });
    Object.values(byLoc).forEach((arr) => arr.sort((a, b) => (a.device_code || "").localeCompare(b.device_code || "")));
    const locs = Object.keys(byLoc).sort().map((id) => ({ id, name: nm[id] || "" }));
    return { locName: nm, devicesByLoc: byLoc, locations: locs };
  }, [devices]);

  const counts = useMemo(() => {
    const c = { all: tickets.length, open: 0, in_progress: 0, waiting: 0, closed: 0, overdue: 0, slaBreach: 0 };
    tickets.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; if (isOverdue(t)) c.overdue++; if (t.status !== "closed" && (t.sla_customer_breached || t.sla_internal_breached)) c.slaBreach++; });
    c.open = tickets.filter((t) => t.status !== "closed").length;
    return c;
  }, [tickets]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    let list = tickets.slice();
    if (statusF === "open") list = list.filter((t) => t.status !== "closed");
    else if (statusF !== "all") list = list.filter((t) => t.status === statusF);
    if (catF !== "all") list = list.filter((t) => t.category === catF);
    if (prioF !== "all") list = list.filter((t) => t.priority === prioF);
    if (q) list = list.filter((t) => [t.title, t.note, t.location_id, locName[t.location_id], t.device_code, t.assignee].join(" ").toLowerCase().includes(q));
    const byCreated = (a, b) => String(a.created_at).localeCompare(String(b.created_at));
    const dueKey = (t) => t.due_date || "9999-12-31";
    if (sortBy === "eingang_neu") list.sort((a, b) => byCreated(b, a));
    else if (sortBy === "eingang_alt") list.sort(byCreated);
    else if (sortBy === "faellig") list.sort((a, b) => { const o = (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0); return o !== 0 ? o : dueKey(a).localeCompare(dueKey(b)); });
    else list.sort(sortTickets); // overdue/prio Standard
    return list;
  }, [tickets, statusF, catF, prioF, q, locName, sortBy]);

  const openEdit = (t) => { setEditing(t); setDlgOpen(true); };
  const openNew = () => { setEditing(null); setDlgOpen(true); };

  const selCls = "bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500/50";

  const statusTiles = [
    { id: "open", label: de ? "Offen (aktiv)" : "Open (active)", n: counts.open, cls: "text-sky-700" },
    { id: "in_progress", label: de ? "In Arbeit" : "In progress", n: counts.in_progress || 0, cls: "text-amber-700" },
    { id: "waiting", label: de ? "Wartet" : "Waiting", n: counts.waiting || 0, cls: "text-slate-700" },
    { id: "closed", label: de ? "Erledigt" : "Done", n: counts.closed || 0, cls: "text-[#c00000]" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <Ticket className="w-6 h-6 text-amber-600" />
            <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
            {counts.overdue > 0 && <span className="text-xs font-mono px-2 py-0.5 rounded border text-red-700 bg-red-500/15 border-red-500/30">{counts.overdue} {de ? "überfällig" : "overdue"}</span>}
            {counts.slaBreach > 0 && <span data-testid="ticket-sla-breach-count" className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border text-red-700 bg-red-500/15 border-red-500/40"><Timer className="w-3 h-3" />{counts.slaBreach} SLA</span>}
          </div>
          {canNewLoc && (
          <button data-testid="ticket-newloc-btn" onClick={() => setNewLocDlg(true)} className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 rounded-lg px-3 py-2 text-sm font-medium transition-colors">
            <Building2 className="w-4 h-4" /> {de ? "Neuer Standort" : "New location"}
          </button>
          )}
          {canSla && (
          <button data-testid="ticket-sla-btn" onClick={() => setSlaDlg(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg px-3 py-2 text-sm transition-colors">
            <Timer className="w-4 h-4" /> {de ? "SLA" : "SLA"}
          </button>
          )}
          {canEditTickets && (
          <button data-testid="ticket-titles-btn" onClick={() => setTitlesDlg(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg px-3 py-2 text-sm transition-colors">
            <Tags className="w-4 h-4" /> {de ? "Titel verwalten" : "Manage titles"}
          </button>
          )}
          {canEditTickets && (
          <button data-testid="ticket-new-btn" onClick={openNew} className="flex items-center gap-2 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors">
            <Plus className="w-4 h-4" /> {de ? "Neues Ticket" : "New ticket"}
          </button>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-5">{de ? "Zentrale Übersicht aller Standort-Tickets und To-dos." : "Central overview of all location tickets and to-dos."}</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {statusTiles.map((s) => (
            <button key={s.id} data-testid={`ticket-tile-${s.id}`} onClick={() => setStatusF(s.id)}
              className={`text-left bg-gray-50 border rounded-xl p-3 transition-colors ${statusF === s.id ? "border-amber-500/50" : "border-gray-200 hover:border-gray-300"}`}>
              <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600">{s.label}</div>
              <div className={`text-2xl font-bold ${s.cls}`}>{s.n}</div>
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-gray-600 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="ticket-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={de ? "Suche (Titel, Standort, Gerät…)" : "Search…"} className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:border-amber-500/50" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="ticket-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={selCls}>
              <option value="all" className="bg-gray-50">{de ? "Alle Status" : "All statuses"}</option>
              <option value="open" className="bg-gray-50">{de ? "Nur offene" : "Open only"}</option>
              {TICKET_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-gray-50">{de ? s.de : s.en}</option>)}
            </select>
            <select data-testid="ticket-filter-category" value={catF} onChange={(e) => setCatF(e.target.value)} className={selCls}>
              <option value="all" className="bg-gray-50">{de ? "Alle Kategorien" : "All categories"}</option>
              {TICKET_CATEGORY.map((c) => <option key={c.id} value={c.id} className="bg-gray-50">{de ? c.de : c.en}</option>)}
            </select>
            <select data-testid="ticket-filter-priority" value={prioF} onChange={(e) => setPrioF(e.target.value)} className={selCls}>
              <option value="all" className="bg-gray-50">{de ? "Alle Prioritäten" : "All priorities"}</option>
              {TICKET_PRIORITY.map((p) => <option key={p.id} value={p.id} className="bg-gray-50">{de ? p.de : p.en}</option>)}
            </select>
            <select data-testid="ticket-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selCls}>
              <option value="overdue" className="bg-gray-50">{de ? "Überfällig & Priorität zuerst" : "Overdue & priority first"}</option>
              <option value="eingang_neu" className="bg-gray-50">{de ? "Eingang: neueste zuerst" : "Created: newest first"}</option>
              <option value="eingang_alt" className="bg-gray-50">{de ? "Eingang: älteste zuerst" : "Created: oldest first"}</option>
              <option value="faellig" className="bg-gray-50">{de ? "Fälligkeit (überfällig zuerst)" : "Due date (overdue first)"}</option>
            </select>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="ticket-view-toggle">
              <button data-testid="ticket-view-cards" onClick={() => { setView("cards"); localStorage.setItem("tickets_view", "cards"); }}
                title={de ? "Kachelansicht" : "Card view"}
                className={`px-2.5 py-2 transition-colors ${view === "cards" ? "bg-amber-500/90 text-black" : "text-gray-700 hover:bg-gray-50"}`}>
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button data-testid="ticket-view-table" onClick={() => { setView("table"); localStorage.setItem("tickets_view", "table"); }}
                title={de ? "Tabellenansicht" : "Table view"}
                className={`px-2.5 py-2 transition-colors ${view === "table" ? "bg-amber-500/90 text-black" : "text-gray-700 hover:bg-gray-50"}`}>
                <Table2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : shown.length === 0 ? (
          <div className="text-gray-600 text-sm py-12 text-center">{de ? "Keine Tickets in dieser Ansicht." : "No tickets."}</div>
        ) : view === "table" ? (
          <TicketTable tickets={shown} users={users} locName={locName} onOpen={setDetail} onEdit={openEdit} onChanged={load} de={de} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tickets-list">
            {shown.map((t) => (
              <TicketCard key={t.id} ticket={t} users={users} showLocation locationName={locName[t.location_id]} onEdit={openEdit} onChanged={load} />
            ))}
          </div>
        )}
      </div>

      <TicketTitlesDialog open={titlesDlg} onClose={() => setTitlesDlg(false)} />
      <SlaDialog open={slaDlg} onClose={() => setSlaDlg(false)} de={de} />
      <NewLocationDialog open={newLocDlg} onClose={() => setNewLocDlg(false)} onSaved={load} de={de} />
      <TicketDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onSaved={load} initial={editing} locations={locations} devicesByLoc={devicesByLoc} users={users} />
      {detail && (
        <TicketDetailDialog
          ticket={detail}
          users={users}
          locName={locName}
          onClose={() => setDetail(null)}
          onEdit={(t) => { setDetail(null); openEdit(t); }}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}
